import AsyncComponent from '@/components/common/AsyncComponent';
import ContextMenu from '@/components/common/ContextMenu';
import CopyButton from '@/components/common/CopyButton';
import CurrencyIcon from '@/components/common/CurrencyIcon';
import Divider from '@/components/common/Divider';
import Loader from '@/components/common/Loader';
import FilterSelect from '@/components/inputs/FilterSelect';
import { BankAccountsResponse } from '@/types/sso';
import { addSpacingToCharacter } from '@/utils/format';
import useContextMenu from '@/utils/hooks/useContextMenu';
import useLanguage from '@/utils/hooks/useLanguage';
import { useSso } from '@/utils/providers/SSOProvider';
import cn from 'classnames';
import QRCode from 'qrcode';
import { Fragment, useEffect, useMemo, useState } from 'react';
import {
    MdCheck,
    MdChevronLeft,
    MdChevronRight,
    MdContentCopy,
    MdOutlineAccountBalance,
    MdOutlineAccountBalanceWallet,
    MdOutlineQrCodeScanner
} from 'react-icons/md';
import Modal, { ModalClose, ModalHeader, ModalProps, ModalTitle } from '../Modal';

interface ModifiedWalletSSI {
    assetType: string;
    currencyId: number;
    currencyName: string;
    digitalAssetName: string;
    active: boolean;
    friendlyName: string;
    networkName: string;
    walletAddress: string;
    walletId: number;
}

export interface PortalSSIModalProps extends ModalProps {}

const defaultFiatCurrency = { label: 'Please select a currency', value: undefined };

const maskAccount = (value?: string) => {
    if (!value) return '';

    const last4 = value.slice(-4);

    const masked = '•'.repeat(Math.max(0, value.length - 4));

    return masked ? `${masked} ${last4}` : last4;
};

export const PortalSSIModal = (props: PortalSSIModalProps) => {
    const { opened } = props;

    const lang = useLanguage();
    const { foundEntity, currenciesApi, ssiProfileBankInfoApi, ssiProfileWalletInfoApi } = useSso();

    const [currencyType, setCurrencyType] = useState<'Fiat' | 'Crypto' | ''>('');
    const [fiatCurrency, setFiatCurrency] = useState<any>(defaultFiatCurrency);

    const { data: cashEntryCurrencies, isLoading: cashEntryCurrenciesLoading } = currenciesApi;
    const { data: bankInfoSSIData, isLoading: isLoadingFiatSSI } = ssiProfileBankInfoApi;
    const { data: walletSSIData, isLoading: isLoadingCryptoSSI } = ssiProfileWalletInfoApi;

    const bankInfoSSIProfiles = useMemo(() => {
        if (bankInfoSSIData && bankInfoSSIData.length > 0) {
            return bankInfoSSIData
                .map((ssi) => ssi.profileInfos)
                .flat()
                .filter((ssi) => ssi.active);
        }
        return [];
    }, [bankInfoSSIData]);

    const hasBankInfoSSI = useMemo(() => bankInfoSSIProfiles.length > 0, [bankInfoSSIProfiles]);

    const walletSSIProfiles = useMemo(() => {
        if (walletSSIData && walletSSIData.length > 0) {
            const returnValues = walletSSIData
                .flatMap((ssi) => ssi.profileInfos)
                .filter((ssi) => ssi.active)
                .flatMap((profile) => {
                    const { supportedAssets, ...restProfiles } = profile;
                    return profile.supportedAssets.map((asset) => ({
                        ...restProfiles,
                        ...asset
                    }));
                });

            returnValues.sort((a, b) => a.digitalAssetName.localeCompare(b.digitalAssetName));
            return returnValues;
        }
        return [];
    }, [walletSSIData]);

    const hasWalletSSI = useMemo(() => walletSSIProfiles.length > 0, [walletSSIProfiles]);

    const currencyOptions = useMemo(() => {
        const uniqueCurrencies = bankInfoSSIProfiles.map((ssi) => ssi.baseCurrencyId);
        return bankInfoSSIProfiles && cashEntryCurrencies
            ? cashEntryCurrencies
                  .filter((ccy) => ccy.enabledForDeposit && ccy.type === 'FIAT' && uniqueCurrencies.includes(ccy.id))
                  .map((currency) => {
                      return {
                          label: currency.digitalAssetName || currency.name,
                          value: currency.id
                      };
                  })
            : [];
    }, [bankInfoSSIProfiles, cashEntryCurrencies]);

    useEffect(() => {
        if (currencyOptions && currencyOptions.length > 0 && currencyType === 'Fiat') {
            const usd = currencyOptions.find((ccy) => ccy.label === 'USD');
            if (usd) setFiatCurrency(usd);
            else setFiatCurrency(currencyOptions[0]);
        }
    }, [currencyOptions, currencyType]);

    useEffect(() => {
        if (opened) {
            currenciesApi.mutate();
            ssiProfileBankInfoApi.mutate();
            ssiProfileWalletInfoApi.mutate();
        }
    }, [opened]);

    return (
        <Modal {...props} className="h-auto" size={currencyType === 'Crypto' ? 'max-w-[900px]' : 'max-w-[650px]'}>
            <ModalHeader>
                <ModalTitle>
                    <div className="flex items-center gap-2">
                        {currencyType !== '' && (
                            <MdChevronLeft
                                className="w-5 h-5 cursor-pointer"
                                onClick={() => {
                                    setCurrencyType('');
                                    setFiatCurrency(defaultFiatCurrency);
                                }}
                            />
                        )}
                        {lang.portal.depositFunds}
                    </div>
                </ModalTitle>
                <ModalClose {...props} />
            </ModalHeader>
            <Divider neutral />
            <div className="py-4 w-full h-full max-h-[600px] overflow-y-auto px-2 sm:px-4">
                {currencyType === '' && (
                    <div className=" flex flex-col gap-3 text-brand-text">
                        <AsyncComponent isLoading={isLoadingFiatSSI || isLoadingCryptoSSI}>
                            <div
                                title={hasBankInfoSSI ? '' : 'Your account does not support fiat deposits.'}
                                className={cn(
                                    'relative text-brand-text flex gap-3 border border-brand-neutral-700 p-4 h-30 rounded-md w-full',
                                    {
                                        'group hover:text-brand-text hover:border-brand-neutral-600 cursor-pointer':
                                            hasBankInfoSSI,
                                        'opacity-50 cursor-not-allowed': !hasBankInfoSSI
                                    }
                                )}
                                onClick={() => {
                                    if (hasBankInfoSSI) setCurrencyType('Fiat');
                                }}>
                                <span>
                                    <MdOutlineAccountBalance className="w-5 h-5 mt-1" />
                                </span>
                                <div className="flex flex-col">
                                    <span className="text-brand-neutral-300 font-semibold text-sm md:text-base">
                                        {lang.portal.fiatViaBankTransfer}
                                    </span>
                                    <span className="group-hover:text-brand-neutral-400 text-xs md:text-sm text-brand-neutral-500 pr-5">
                                        {lang.portal.makeADepositFromYourWhitelistedBankAccount}
                                    </span>
                                </div>
                                <MdChevronRight className="w-6 h-6 absolute right-4 self-center" />
                            </div>
                            <div
                                title={hasWalletSSI ? '' : 'Your account does not support crypto deposits.'}
                                className={cn(
                                    'relative text-brand-text flex gap-3 border border-brand-neutral-700 p-4 h-30 rounded-md w-full',
                                    {
                                        'group hover:text-brand-text hover:border-brand-neutral-600 cursor-pointer':
                                            hasWalletSSI,
                                        'opacity-50 cursor-not-allowed': !hasWalletSSI
                                    }
                                )}
                                onClick={() => {
                                    if (hasWalletSSI) setCurrencyType('Crypto');
                                }}>
                                <span>
                                    <MdOutlineAccountBalanceWallet className="w-5 h-5 mt-1" />
                                </span>
                                <div className="flex flex-col gap-1">
                                    <span className="text-brand-neutral-300 font-semibold text-sm md:text-base">
                                        {lang.portal.cryptoViaWalletAddress}
                                    </span>
                                    <span className="group-hover:text-brand-neutral-400 text-xs md:text-sm text-brand-neutral-500 pr-5">
                                        {lang.portal.makeADepositToYourWhitelistedWalletAddress}
                                    </span>
                                </div>
                                <MdChevronRight className="w-6 h-6 absolute right-4 self-center" />
                            </div>
                        </AsyncComponent>
                    </div>
                )}
                {currencyType === 'Fiat' && (
                    <div className="flex flex-col h-full gap-3 text-brand-text">
                        <div className="flex flex-col gap-1 text-sm">
                            <span className="text-sm">{lang.portal.depositFiatDescription}</span>
                            <AsyncComponent isLoading={cashEntryCurrenciesLoading}>
                                <FilterSelect
                                    selected={fiatCurrency}
                                    options={currencyOptions}
                                    enableCurrencyIcon
                                    enableSorting
                                    onChange={(e) => setFiatCurrency(e)}
                                />
                            </AsyncComponent>
                        </div>
                        <span className="text-sm">{lang.portal.selectedBankAccountToViewDetails}</span>
                        {isLoadingFiatSSI ? (
                            <Loader className="w-4 h-4" />
                        ) : (
                            <Fragment>
                                {bankInfoSSIProfiles.length > 0 && typeof fiatCurrency?.value === 'number' && (
                                    <BankAccountInfo
                                        ssiDatas={bankInfoSSIProfiles}
                                        selectedCurrency={fiatCurrency.value}
                                    />
                                )}
                            </Fragment>
                        )}
                    </div>
                )}
                {currencyType === 'Crypto' && (
                    <div className="flex flex-col h-full text-brand-text">
                        <span className="text-sm">{lang.portal.depositCryptoDescription}</span>
                        <div className="flex text-sm font-semibold pt-5 py-2 border-b border-brand-neutral-400">
                            <div className="flex-1 basis-0"> {lang.portal.token}</div>
                            <div className="flex-1 basis-0">{lang.portal.network}</div>
                            <div className="flex-1 basis-0">{lang.portal.chain}</div>
                            <div className="flex-[4_1_0] basis-0 hidden md:block">{lang.portal.address}</div>
                        </div>
                        {isLoadingCryptoSSI ? (
                            <Loader className="w-4 h-4 mt-2" />
                        ) : (
                            <WalletAddressInfo walletSSIProfiles={walletSSIProfiles} />
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
};

type WalletAddressInfoProps = {
    walletSSIProfiles: ModifiedWalletSSI[];
};

const WalletAddressInfo = ({ walletSSIProfiles }: WalletAddressInfoProps) => {
    const [qrUrl, setQrUrl] = useState('');

    const lang = useLanguage();

    const contextMenu = useContextMenu();
    const { contextMenuX, contextMenuY, contextMenuVisible, handleOpenContextMenu, handleCloseContextMenu } =
        contextMenu;

    return (
        <div className="flex flex-col text-sm text-brand-text divide-y divide-brand-neutral-500 gap-4 md:gap-2 pb-4">
            {walletSSIProfiles.length > 0 ? (
                walletSSIProfiles.map((profile, index) => {
                    const name = profile.digitalAssetName || profile.currencyName;
                    return (
                        <div key={name + index} className="flex pt-3 md:pt-1.5">
                            <div className="flex flex-col gap-2 md:gap-0 w-full">
                                <div className="flex">
                                    <div className="flex-1 basis-0 flex items-center">
                                        <CurrencyIcon
                                            ccy={profile.digitalAssetName || profile.currencyName}
                                            className="w-4 h-4 mr-1.5"
                                        />
                                        {profile.digitalAssetName || profile.currencyName}
                                    </div>
                                    <div className="flex-1 basis-0 flex items-center">{profile.networkName}</div>
                                    <div className="flex-1 basis-0 flex items-center">{profile.assetType}</div>
                                    <div className="flex-[4_1_0] basis-0 items-center gap-3 overflow-auto hidden md:flex">
                                        <CopyButton value={profile.walletAddress}>
                                            {({ copied, copy }) => (
                                                <button
                                                    onClick={copy}
                                                    className="border border-brand-neutral-500 hover:border-brand-neutral-400 rounded-sm p-1">
                                                    {copied ? (
                                                        <MdCheck className="w-4 h-4 text-brand-text" />
                                                    ) : (
                                                        <MdContentCopy className="w-4 h-4 text-brand-text" />
                                                    )}
                                                </button>
                                            )}
                                        </CopyButton>
                                        <span className="flex-1 basis-0 pt-1.5 pb-1 break-all">
                                            {profile.walletAddress}
                                        </span>
                                        <div
                                            className="border border-brand-neutral-500 hover:border-brand-neutral-400 rounded-sm p-1 cursor-pointer"
                                            onClick={(e) => {
                                                QRCode.toDataURL(profile.walletAddress, { width: 300 })
                                                    .then((url) => setQrUrl(url))
                                                    .catch((err) => console.error(err));
                                                handleOpenContextMenu(e);
                                            }}>
                                            <MdOutlineQrCodeScanner className="w-4 h-4 text-brand-text" />
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 overflow-auto md:hidden">
                                    <CopyButton value={profile.walletAddress}>
                                        {({ copied, copy }) => (
                                            <span className="flex-1 basis-0 break-all" onClick={copy}>
                                                <div className="flex items-center gap-1.5 text-xs text-brand-neutral-400 border p-2 rounded-sm border-brand-neutral-600">
                                                    <span>
                                                        {copied ? (
                                                            <MdCheck className="w-4 h-4 text-brand-text" />
                                                        ) : (
                                                            <MdContentCopy className="w-4 h-4 text-brand-text" />
                                                        )}
                                                    </span>
                                                    {profile.walletAddress}
                                                </div>
                                            </span>
                                        )}
                                    </CopyButton>
                                </div>
                            </div>
                        </div>
                    );
                })
            ) : (
                <div className="flex pt-2">{lang.portal.noSupportedAssets}</div>
            )}
            <ContextMenu
                x={contextMenuX}
                y={contextMenuY}
                offset={{ x: 16, y: 16 }}
                visible={contextMenuVisible}
                onClose={handleCloseContextMenu}
                className="!bg-brand-background !w-auto !z-[100]">
                {qrUrl && (
                    <div className="p-2">
                        <img src={qrUrl} alt="QR Code" className="w-48 h-48" />
                    </div>
                )}
            </ContextMenu>
        </div>
    );
};

const BankAccountInfo = ({
    ssiDatas,
    selectedCurrency
}: {
    ssiDatas: BankAccountsResponse[];
    selectedCurrency: number;
}) => {
    const lang = useLanguage();

    const ssiProfilesBasedOnSelectedCurrency = useMemo(() => {
        return ssiDatas.filter((ssi) => ssi.baseCurrencyId === selectedCurrency);
    }, [selectedCurrency, ssiDatas]);

    const [selectedSsiProfile, setSelectedSsiProfile] = useState<BankAccountsResponse>();

    const { hasBankInfo, hasCorrespondingBank, hasIntermediaryBank } = useMemo(() => {
        return {
            hasBankInfo:
                selectedSsiProfile?.bankName ||
                selectedSsiProfile?.bankAddress ||
                selectedSsiProfile?.branchName ||
                selectedSsiProfile?.accountNumber ||
                selectedSsiProfile?.ibanCode ||
                selectedSsiProfile?.swiftCode,
            hasCorrespondingBank:
                selectedSsiProfile?.correspondentBankName || selectedSsiProfile?.correspondentBankSwiftCode,
            hasIntermediaryBank:
                selectedSsiProfile?.intermediaryBankName || selectedSsiProfile?.intermediaryBankSwiftCode
        };
    }, [selectedSsiProfile]);

    const ssiNotes = useMemo(() => {
        return (
            selectedSsiProfile &&
            selectedSsiProfile.note?.split('\n').map((line, index) => (
                <p key={index} className="pb-2 last:pb-0">
                    {line}
                </p>
            ))
        );
    }, [selectedSsiProfile?.note]);

    useEffect(() => {
        // Reset selected SSI profile when selected currency changes
        setSelectedSsiProfile(undefined);
    }, [selectedCurrency]);

    useEffect(() => {
        // If there's only 1 SSI profile for the selected currency, select it by default
        if (ssiProfilesBasedOnSelectedCurrency && ssiProfilesBasedOnSelectedCurrency.length === 1) {
            setSelectedSsiProfile(ssiProfilesBasedOnSelectedCurrency[0]);
        } else {
            setSelectedSsiProfile(undefined);
        }
    }, [ssiProfilesBasedOnSelectedCurrency]);

    return (
        <>
            {ssiProfilesBasedOnSelectedCurrency?.length ? (
                <SSIBankProfileOptions
                    ssiProfiles={ssiProfilesBasedOnSelectedCurrency}
                    selectedBankInfo={selectedSsiProfile}
                    setSelectedBankInfo={setSelectedSsiProfile}
                />
            ) : (
                <span className="text-sm text-brand-neutral-200">{lang.portal.noBankAccountInformationAvailable}</span>
            )}

            {selectedSsiProfile && (
                <div className="transition-all duration-200 ease-in-out">
                    <span className="text-sm">{lang.portal.transferFundsFromYourWhitelistedBankAccount}</span>
                    <div className="flex flex-col gap-4 py-2">
                        {hasBankInfo && (
                            <div>
                                <span className="font-bold">{lang.portal.bankDetails}</span>
                                <div className="flex flex-col gap-1 divide-y divide-brand-neutral-500">
                                    {selectedSsiProfile?.beneficiaryName && (
                                        <InfoEntry
                                            label={lang.portal.beneficiaryName}
                                            value={selectedSsiProfile?.beneficiaryName}
                                        />
                                    )}

                                    {selectedSsiProfile?.entityAddress && (
                                        <InfoEntry
                                            label={lang.portal.beneficiaryAddress}
                                            value={selectedSsiProfile?.entityAddress}
                                        />
                                    )}
                                    <InfoEntry label={lang.portal.bankName} value={selectedSsiProfile?.bankName} />
                                    <InfoEntry
                                        label={lang.portal.bankAddress}
                                        value={selectedSsiProfile?.bankAddress}
                                    />
                                    <InfoEntry label={lang.portal.branchName} value={selectedSsiProfile?.branchName} />
                                    <InfoEntry
                                        label={lang.portal.accountNumber}
                                        value={selectedSsiProfile?.accountNumber}
                                    />
                                    <InfoEntry
                                        label={lang.portal.ibanCode}
                                        htmlElement={addSpacingToCharacter(selectedSsiProfile?.ibanCode, 4) as any}
                                        value={selectedSsiProfile?.ibanCode}
                                    />
                                    <InfoEntry label={lang.portal.swiftCode} value={selectedSsiProfile?.swiftCode} />
                                    {selectedSsiProfile?.note && (
                                        <InfoEntry
                                            label={lang.portal.notes}
                                            value={selectedSsiProfile?.note}
                                            hideCopyButton
                                            htmlElement={ssiNotes}
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                        {hasCorrespondingBank && (
                            <div>
                                <span className="font-bold">{lang.portal.correspondentBankDetails}</span>
                                <div className="flex flex-col gap-1 divide-y divide-brand-neutral-500">
                                    <InfoEntry
                                        label={lang.portal.correspondentBankName}
                                        value={selectedSsiProfile?.correspondentBankName}
                                    />
                                    <InfoEntry
                                        label={lang.portal.correspondentSwiftCode}
                                        value={selectedSsiProfile?.correspondentBankSwiftCode}
                                    />
                                </div>
                            </div>
                        )}
                        {hasIntermediaryBank && (
                            <div>
                                <span className="font-bold">{lang.portal.intermediaryBankDetails}</span>
                                <div className="flex flex-col gap-1 divide-y divide-brand-neutral-500">
                                    <InfoEntry
                                        label={lang.portal.intermediaryBankName}
                                        value={selectedSsiProfile?.intermediaryBankName}
                                    />
                                    <InfoEntry
                                        label={lang.portal.intermediarySwiftCode}
                                        value={selectedSsiProfile?.intermediaryBankSwiftCode}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

const InfoEntry = ({
    label,
    value,
    hideCopyButton = false,
    htmlElement
}: {
    label: string;
    value?: string;
    hideCopyButton?: boolean;
    htmlElement?: JSX.Element | JSX.Element[];
}) => {
    return (
        <>
            {value && (
                <div className="flex gap-2 pt-1">
                    <span className="text-sm flex-1 basis-0 flex items-center text-brand-neutral-400">{label}</span>
                    <div className="text-sm flex-1 basis-0 flex gap-2 items-center overflow-auto">
                        {!hideCopyButton ? (
                            <CopyButton value={value}>
                                {({ copied, copy }) => (
                                    <button
                                        onClick={copy}
                                        className="border border-brand-neutral-500 hover:border-brand-neutral-400 rounded-sm p-1">
                                        {copied ? (
                                            <MdCheck className="w-4 h-4 text-brand-text" />
                                        ) : (
                                            <MdContentCopy className="w-4 h-4 text-brand-text" />
                                        )}
                                    </button>
                                )}
                            </CopyButton>
                        ) : null}

                        <span className="flex-1 basis-0 pt-1.5 pb-1 break-all">{htmlElement || value}</span>
                    </div>
                </div>
            )}
        </>
    );
};

interface SSIBankProfileOptionsProps {
    noMask?: boolean;
    ssiProfiles: any[];
    selectedBankInfo: any;
    setSelectedBankInfo: (bankInfo: any) => void;
}

export const SSIBankProfileOptions = (props: SSIBankProfileOptionsProps) => {
    const { noMask, ssiProfiles, selectedBankInfo, setSelectedBankInfo } = props;
    return (
        <div className="flex gap-3 flex-wrap">
            {ssiProfiles.map((ssiProfile) => {
                const isActive =
                    selectedBankInfo?.baseCurrencyId === ssiProfile.baseCurrencyId &&
                    selectedBankInfo?.swiftCode === ssiProfile.swiftCode &&
                    (selectedBankInfo?.ibanCode
                        ? selectedBankInfo.ibanCode === ssiProfile.ibanCode
                        : selectedBankInfo?.accountNumber === ssiProfile.accountNumber);

                const maskedAccountValue = ssiProfile.ibanCode
                    ? addSpacingToCharacter(maskAccount(ssiProfile.ibanCode), 4)
                    : maskAccount(ssiProfile.accountNumber);

                return (
                    <button
                        key={`${ssiProfile.baseCurrencyId}-${ssiProfile.swiftCode}-${
                            ssiProfile.ibanCode || ssiProfile.accountNumber
                        }`}
                        type="button"
                        aria-pressed={isActive}
                        onClick={() => setSelectedBankInfo(ssiProfile)}
                        className={cn(
                            'flex flex-col justify-between rounded-md border text-left bg-background-dark transition-colors duration-150 hover:border-brand-primary-light',
                            {
                                'border-brand-primary-light bg-background': isActive,
                                'border-brand-neutral-700': !isActive,
                                'min-w-[220px] p-4': !noMask,
                                'p-2': noMask
                            }
                        )}>
                        <div className="flex justify-between items-start gap-2">
                            <span
                                className={cn('text-sm text-brand-neutral-200', {
                                    'font-semibold': !noMask
                                })}>
                                {ssiProfile.bankName}
                            </span>
                        </div>
                        {!noMask && (
                            <div className="mt-3 text-xs text-brand-neutral-400 flex justify-end items-center">
                                <span className=" font-medium tracking-widest">{maskedAccountValue}</span>
                            </div>
                        )}
                    </button>
                );
            })}
        </div>
    );
};
